import {
	configureApiPortalBrowserSdk,
	createApiPortalQueryClient
} from '@repo/api-portal-sdk'
import type { QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { type AppLanguageCode, getInitialAppLanguage } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import { routeTree } from './routeTree.gen'

export interface RouterContext {
	appLanguage: AppLanguageCode
	queryClient: QueryClient
}

export function getRouter() {
	const queryClient = createApiPortalQueryClient()
	const router = createRouter({
		routeTree,
		context: {
			appLanguage: getInitialAppLanguage(),
			queryClient
		} satisfies RouterContext,
		defaultPreload: 'intent',
		defaultPreloadStaleTime: 0,
		scrollRestoration: true
	})

	if (!router.isServer) {
		configureApiPortalBrowserSdk({
			queryClient,
			getCsrfToken: (client) =>
				client.getQueryData<{ csrfToken?: string }>(
					makeQueryKeys.auth.session()
				)?.csrfToken,
			isSessionAuthenticated: (client) =>
				client.getQueryData<{ sessionType?: string }>(
					makeQueryKeys.auth.session()
				)?.sessionType === 'authenticated',
			onUnauthorized: () => router.invalidate()
		})
	}

	setupRouterSsrQueryIntegration({ router, queryClient })

	return router
}

declare module '@tanstack/react-router' {
	interface Register {
		router: ReturnType<typeof getRouter>
	}
}
